// GAVION booking (spec §2): one week of free slots at a time, grouped by day in the viewer's zone, paging forward up to
// the horizon. `exclude`: slots the page already knows are gone (a 409 « Ce créneau vient d'être pris »).
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { BOOKING_COPY, fill, type PublicLang } from "./copy";
import { browserZone, groupSlots, zoneLabel } from "./slotGroups";
import { type PublicBookingApi, PublicBookingError, type PublicRep, type SlotPage } from "./api/types";

const WEEK = 7 * 86_400_000;
type Query = { slug: string } | { token: string };
type State = { kind: "loading" } | { kind: "ready"; page: SlotPage } | { kind: "error"; code: string };

export const SlotPicker = ({
  api,
  query,
  lang,
  exclude = [],
  onPick,
  onRep,
  onError,
}: {
  api: PublicBookingApi;
  query: Query;
  lang: PublicLang;
  exclude?: readonly string[];
  onPick: (iso: string) => void;
  onRep?: (rep: PublicRep) => void;
  onError?: (code: string) => void;
}) => {
  const c = BOOKING_COPY[lang];
  const tz = browserZone();
  const [from, setFrom] = useState(() => new Date().toISOString());
  const [back, setBack] = useState<string[]>([]);
  const [state, setState] = useState<State>({ kind: "loading" });
  const [attempt, setAttempt] = useState(0);
  // The key never holds the token in a place that is logged: it is only a React dependency.
  const key = "slug" in query ? `slug:${query.slug}` : `token:${query.token}`;

  useEffect(() => {
    let alive = true;
    setState({ kind: "loading" });
    const to = new Date(Date.parse(from) + WEEK).toISOString();
    api.slots({ ...query, from, to }).then(
      (page) => {
        if (!alive) return;
        setState({ kind: "ready", page });
        onRep?.(page.rep);
      },
      (e: unknown) => {
        if (!alive) return;
        const code = e instanceof PublicBookingError ? e.code : "error";
        setState({ kind: "error", code });
        onError?.(code);
      },
    );
    return () => {
      alive = false;
    };
    // `query` is identified by `key`; the callbacks are not inputs of the fetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, key, from, attempt]);

  if (state.kind === "loading") return <p role="status">{c.loading}</p>;
  if (state.kind === "error") {
    if (state.code === "not_found" || state.code === "link_expired") return null;
    return (
      <div role="alert" className="flex flex-col items-start gap-2">
        <p>{state.code === "rate_limited" ? c.rateLimited : c.unavailable}</p>
        <Button variant="outline" size="sm" onClick={() => setAttempt((n) => n + 1)}>
          {c.retry}
        </Button>
      </div>
    );
  }
  const groups = groupSlots(state.page.slots.filter((s) => !exclude.includes(s)), tz, lang);
  const next = state.page.nextFrom;
  return (
    <section aria-label={c.pick}>
      <p title={tz} className="text-sm text-muted-foreground">
        {fill(c.tz, { tz: zoneLabel(tz, lang) })}
      </p>
      {groups.length === 0 && <p className="mt-4">{c.noSlots}</p>}
      {groups.map((g) => (
        <div key={g.key} role="group" aria-label={g.label} className="mt-4">
          <h2 className="text-sm font-semibold first-letter:uppercase">{g.label}</h2>
          <div className="mt-2 flex flex-wrap gap-2">
            {g.times.map((t) => (
              <Button key={t.iso} variant="outline" size="sm" onClick={() => onPick(t.iso)}>
                {t.label}
              </Button>
            ))}
          </div>
        </div>
      ))}
      <div className="mt-6 flex justify-between">
        <Button
          variant="ghost" className="text-foreground"
          size="sm"
          disabled={back.length === 0}
          onClick={() => {
            setFrom(back[back.length - 1]);
            setBack(back.slice(0, -1));
          }}
        >
          {c.prevWeek}
        </Button>
        <Button
          variant="ghost" className="text-foreground"
          size="sm"
          disabled={!next}
          onClick={() => {
            if (!next) return;
            setBack([...back, from]);
            setFrom(next);
          }}
        >
          {c.nextWeek}
        </Button>
      </div>
    </section>
  );
};
